"use client";

import { useEffect, useState } from "react";
import { devLabel, devLabelPanel } from "@/lib/dev-labels";
import { EditorCore } from "@/core";
import { mediaTimeToSeconds } from "@/wasm";

export interface ProjectCard {
	id: string;
	name: string;
	meta: string;
	score: number;
	thumbTime: string;
	thumbBg?: string;
}

/** seconds -> "m:ss". */
function fmtTime(seconds: number): string {
	const s = Math.max(0, Math.round(seconds));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export interface ProjectsListProps {
	/** Override list (tests). When omitted, real projects load from storage. */
	projects?: ProjectCard[];
}

/**
 * ProjectsList — Increment 18. Loads the user's REAL saved projects from the
 * EditorCore project manager (was a hardcoded 3-item PROJECTS const).
 */
export function ProjectsList({ projects: override }: ProjectsListProps) {
	const [loaded, setLoaded] = useState<ProjectCard[]>([]);

	useEffect(() => {
		if (override) return;
		let cancelled = false;
		(async () => {
			try {
				const editor = EditorCore.getInstance();
				await editor.project.loadAllProjects();
				if (cancelled) return;
				const cards = editor.project.getSavedProjects().map((p) => {
					const dur = mediaTimeToSeconds({ time: p.duration });
					return {
						id: p.id,
						name: p.name,
						meta: `${new Date(p.updatedAt).toLocaleDateString()} | ${fmtTime(dur)}`,
						score: 0,
						thumbTime: fmtTime(dur),
					} satisfies ProjectCard;
				});
				setLoaded(cards);
			} catch (e) {
				console.error("[ProjectsList] load failed", e);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [override]);

	const projects = override ?? loaded;

	return (
		<section {...devLabelPanel("13 · Projects")}>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: 10,
				}}
			>
				<div style={{ fontSize: 14, fontWeight: 600 }}>
					프로젝트{" "}
					<span
						style={{
							fontWeight: 400,
							opacity: 0.55,
							fontSize: 11,
							marginLeft: 4,
						}}
					>
						{projects.length}
					</span>
				</div>
				<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
					<ProjIconBtn devLabel="13-A" title="검색">
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
							<circle cx="11" cy="11" r="8" />
							<line x1="21" y1="21" x2="16.65" y2="16.65" />
						</svg>
					</ProjIconBtn>
					<ProjIconBtn devLabel="13-B" withLabel>
						목록
						<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
							<polyline points="6 9 12 15 18 9" />
						</svg>
					</ProjIconBtn>
					<ProjIconBtn devLabel="13-C" withLabel>
						<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<polyline points="3 6 5 6 21 6" />
							<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
						</svg>
						휴지통
					</ProjIconBtn>
					<ProjIconBtn devLabel="13-D" withLabel>
						<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<polyline points="23 4 23 10 17 10" />
							<path d="M20.5 15a9 9 0 1 1-2.1-9.4l4.6 4.4" />
						</svg>
						동기화
					</ProjIconBtn>
				</div>
			</div>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))",
					gap: 14,
				}}
			>
				{projects.map((p, i) => (
					<div
						key={p.id}
						{...devLabel(i === 0 ? "13-E" : `13-E-${i}`)}
						style={{
							cursor: "pointer",
							borderRadius: 8,
							overflow: "hidden",
							background: "#1a1a1a",
							border: "1px solid rgba(255,255,255,0.06)",
							transition: "all 0.15s",
						}}
					>
						<div
							style={{
								aspectRatio: "16/9",
								background:
									p.thumbBg ??
									"linear-gradient(135deg,#7a5a3e 0%,#3a2a1a 50%,#1a0a0a 100%)",
								position: "relative",
							}}
						>
							<span
								style={{
									position: "absolute",
									top: 5,
									left: 5,
									padding: "1px 5px",
									fontSize: 9,
									fontWeight: 700,
									background: "rgba(0,0,0,0.7)",
									color: "#d4af37",
									borderRadius: 3,
								}}
							>
								{p.score}
							</span>
							<span
								style={{
									position: "absolute",
									bottom: 5,
									right: 5,
									padding: "1px 5px",
									fontSize: 9,
									fontWeight: 600,
									background: "rgba(0,0,0,0.7)",
									color: "#fff",
									borderRadius: 3,
									fontFamily: "'Geist Mono',monospace",
								}}
							>
								{p.thumbTime}
							</span>
						</div>
						<div style={{ padding: "8px 10px" }}>
							<div
								style={{
									fontSize: 12,
									fontWeight: 500,
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								{p.name}
							</div>
							<div
								style={{
									fontSize: 10,
									color: "#a3a3a3",
									marginTop: 2,
									fontFamily: "'Geist Mono',monospace",
								}}
							>
								{p.meta}
							</div>
						</div>
					</div>
				))}
			</div>
		</section>
	);
}

function ProjIconBtn({
	children,
	devLabel: id,
	withLabel,
	title,
}: {
	children: React.ReactNode;
	devLabel: string;
	withLabel?: boolean;
	title?: string;
}) {
	return (
		<button
			type="button"
			data-dev={process.env.NODE_ENV !== "production" ? id : undefined}
			title={title}
			style={{
				width: withLabel ? "auto" : 28,
				height: 28,
				padding: withLabel ? "0 10px" : 0,
				borderRadius: 6,
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				gap: 4,
				fontSize: withLabel ? 11 : 13,
				fontWeight: 500,
				color: "#a3a3a3",
				cursor: "pointer",
				border: "1px solid rgba(255,255,255,0.08)",
				background: "#1a1a1a",
			}}
		>
			{children}
		</button>
	);
}
